<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrSecondaryButton from "~/components/global/WrSecondaryButton.vue";
import WrVaahAgentsOrbit from "./WrVaahAgentsOrbit.vue";

interface HeroLink {
  label: string;
  href: string;
  icon?: string;
}
interface HeroContent {
  eyebrow?: string;
  heading: string;
  subheading?: string;
  subcopy?: string;
  ctaText?: string;
  cta2Text?: string;
  links?: HeroLink[];
}
interface OrbitTask {
  label: string;
  done: boolean;
}
interface OrbitAgent {
  name: string;
  tag: string;
  status: string;
  icon: string;
  progress: number;
  tasks: OrbitTask[];
}
interface OrbitContent {
  label?: string;
  agents: OrbitAgent[];
}
const props = defineProps<{ hero: HeroContent; orbit?: OrbitContent }>();
const emit = defineEmits<{ "cta-clicked": []; "demo-clicked": [] }>();

/* Highlight the two brand names in the subcopy without using v-html. */
const subcopyParts = computed(() =>
  (props.hero.subcopy || "").split(/(VaahAgents|WebReinvent)/g),
);
</script>

<template>
  <section class="relative pt-32">
    <div class="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="relative z-10 text-center">
        <div
          v-if="hero.eyebrow"
          class="genic-hero-badge mx-auto max-w-[432px] max-sm:max-w-full w-full rounded-[60px] border border-[#885AF51F] bg-[#885AF51A] dark:border-[#FFFFFF14] dark:bg-[#885AF52E] flex flex-row items-center justify-center gap-[10px] max-sm:gap-1 py-2 mb-4">
          <div class="size-[6px] bg-[#935AF5] rounded-full" />
          <p class="font-avant font-medium uppercase text-sm max-sm:text-[12px] text-[#6D28D9] dark:text-[#AA9AFF] text-center pr-1">
            {{ hero.eyebrow }}
          </p>
        </div>
        <NuxtImg
          src="/images/how-we-work/vaah-agent-light.webp"
          alt="VaahAgents"
          loading="eager"
          class="mx-auto mb-2 h-auto w-[140px] lg:w-[212px] va-hero-img-light"
        />
        <NuxtImg
          src="/images/how-we-work/vaah-agent-dark.webp"
          alt="VaahAgents"
          loading="eager"
          class="mx-auto mb-2 h-auto w-[140px] lg:w-[212px] va-hero-img-dark"
        />
        <h1 class="text-center">
          <span class="va-heading block font-avant text-[clamp(18px,5.9vw,56px)] leading-[1.2] font-semibold tracking-0 text-center">{{ hero.heading }}</span>
          <span v-if="hero.subheading" class="block font-avant text-[clamp(20px,4vw,45px)] leading-none font-normal tracking-0 text-center mt-2 text-[#501d71] dark:text-white">{{ hero.subheading }}</span>
        </h1>
        <p v-if="hero.subcopy" class="font-avant text-[clamp(16px,1.8vw,24px)] leading-[1.667] max-w-4xl tracking-[0.02em]  mx-auto mt-5 text-[#444] dark:text-[#bcb2c3]">
          <template v-for="(part, i) in subcopyParts" :key="i">
            <span v-if="part === 'VaahAgents' || part === 'WebReinvent'" class="font-medium text-[#501d71] dark:text-[#6d8bff]">{{ part }}</span>
            <template v-else>{{ part }}</template>
          </template>
        </p>
      </div>

      <div class="text-center mt-10">
        <div class="flex items-center justify-center flex-col md:flex-row gap-3 sm:gap-4">
          <WrButton
            v-if="hero.ctaText"
            @click="emit('cta-clicked')"
            container-class="inline-flex items-center h-[48px] lg:h-[52px] bg-none p-0"
            append-class="px-5 sm:px-7 lg:px-8 bg-[linear-gradient(95.99deg,#6C59B5_3.69%,#261640_96.65%)] dark:bg-[linear-gradient(95.99deg,#586BCC_3.69%,#23216C_96.65%)] text-[14px] sm:text-[15px] lg:text-[16px]"
          >{{ hero.ctaText }}</WrButton>
          <!-- <WrSecondaryButton v-if="hero.cta2Text" @click="emit('demo-clicked')">
            <svg viewBox="0 0 24 24" class="size-4" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
            {{ hero.cta2Text }}
          </WrSecondaryButton> -->
        </div>
      </div>

      <div v-if="hero.links?.length" class="text-center mt-8 mb-16 flex items-center justify-center gap-6">
        <a
          v-for="link in hero.links"
          :key="link.label"
          :href="link.href"
          target="_blank"
          rel="noopener"
          class="inline-flex items-center gap-[7px] text-sm sm:text-base font-medium transition-colors duration-[250ms] ease-in-out text-[#555] hover:text-[#501d71] dark:text-[#c2c6d4] dark:hover:text-white"
        >
          <svg v-if="link.icon === 'github'" viewBox="0 0 24 24" class="size-6" fill="currentColor" aria-hidden="true"><path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.5v-2c-3.2.7-3.9-1.4-3.9-1.4-.5-1.3-1.3-1.7-1.3-1.7-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.6.2 2.8.1 3.1.8.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.2c0 .3.2.6.8.5 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5z" /></svg>
          <svg v-else viewBox="0 0 24 24" class="size-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 13h6" /><path d="M9 17h6" /></svg>
          {{ link.label }}
        </a>
      </div>

      <!-- 4-agent orbital visualization -->
      <WrVaahAgentsOrbit v-if="orbit?.agents?.length" :orbit="orbit" class="mt-14 lg:mt-20" />

    </div>
  </section>
</template>

<style scoped>
/* Hero image: light shows by default, dark under .my-app-dark. Driven by the
   theme class on <html> (not a JS color-mode check) so it matches the real theme
   on first paint and after hydration — no flash of the wrong variant. */
.va-hero-img-dark {
  display: none;
}
.my-app-dark .va-hero-img-light {
  display: none;
}
.my-app-dark .va-hero-img-dark {
  display: block;
}

/* Gradient headline text — gradient + background-clip only; type is Tailwind */
.va-heading {
  background: linear-gradient(92.15deg, #12dbff -3.57%, #895af5 80.72%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}

/* Rotating conic-gradient ring on the hero badge — needs @property + mask-composite.
   Imported verbatim from the GenicUI hero badge (feature/genic-ui, WrGenicHero.vue). */
@property --genic-ring-angle {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: false;
}

.genic-hero-badge {
  position: relative;
}

.genic-hero-badge::after {
  content: "";
  position: absolute;
  /* -1px so the ring's outer edge sits on the badge's border-box (the 1px
     border line) instead of 1px inside it */
  inset: -1px;
  border-radius: 9999px;
  padding: 2px;
  background: conic-gradient(from var(--genic-ring-angle),
        transparent 0deg,
        transparent 320deg,
        #D9D9D9 345deg,
        transparent 360deg);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  animation: genic-ring-rotate 8s linear infinite;
  pointer-events: none;
}

@keyframes genic-ring-rotate {
  to {
    --genic-ring-angle: 360deg;
  }
}

.my-app-light .genic-hero-badge::after {
  background: conic-gradient(from var(--genic-ring-angle),
        transparent 0deg,
        transparent 320deg,
        #885AF5 345deg,
        transparent 360deg);
}

@media (prefers-reduced-motion: reduce) {
  .genic-hero-badge::after {
    animation: none;
  }
}
</style>
